// incidentApi.js - Auto-RCA Incidents API service
import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js';

export const incidentApi = {
  /**
   * List RCA reports with optional filters
   */
  getReports: async ({ severity, alertName, status, fromDate, toDate, search, page = 1, limit = 20 } = {}) => {
    const params = new URLSearchParams();
    if (severity) params.append('severity', severity);
    if (alertName) params.append('alert_name', alertName);
    if (status) params.append('status', status);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (search) params.append('search', search);
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));

    const query = params.toString();
    const url = `${API_BASE_URL}/api/v1/rca/reports${query ? `?${query}` : ''}`;
    const response = await fetchWrapper(url);
    if (!response.ok) throw new Error('Failed to fetch RCA reports');
    return await response.json();
  },

  /**
   * Get a specific RCA report with full content
   */
  getReport: async (reportId) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/reports/${reportId}`);
    if (!response.ok) throw new Error('Failed to fetch RCA report');
    return await response.json();
  },

  /**
   * Search past RCA reports
   */
  searchReports: async (query, limit = 5) => {
    const params = new URLSearchParams({ query, limit: String(limit) });
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/reports/search?${params}`);
    if (!response.ok) throw new Error('Failed to search RCA reports');
    return await response.json();
  },

  /**
   * Get RCA statistics
   * GET /api/v1/rca/stats
   * Returns: { stats: { total_reports, by_severity, unique_alerts, alerts_by_name, last_report } }
   */
  getStats: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/stats`);
    if (!response.ok) throw new Error('Failed to fetch RCA stats');
    return await response.json();
  },

  /**
   * Get similar past incidents for an alert
   */
  getSimilarIncidents: async (alertName, limit = 5) => {
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/rca/similar/${encodeURIComponent(alertName)}?limit=${limit}`
    );
    if (!response.ok) throw new Error('Failed to fetch similar incidents');
    return await response.json();
  },

  /**
   * Mark an incident as resolved
   */
  resolveIncident: async (reportId, { resolutionNotes, resolvedBy }) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/reports/${reportId}/resolve`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        resolution_notes: resolutionNotes,
        resolved_by: resolvedBy,
      }),
    });
    if (!response.ok) throw new Error('Failed to resolve incident');
    return await response.json();
  },

  /**
   * Submit feedback on RCA quality
   */
  submitFeedback: async (reportId, { rating, comment, userId }) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/reports/${reportId}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rating,
        comment,
        user_id: userId,
      }),
    });
    if (!response.ok) throw new Error('Failed to submit feedback');
    return await response.json();
  },

  /**
   * Re-run RCA analysis for an existing incident
   */
  rerunRca: async (reportId) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/reports/${reportId}/rerun`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to re-run RCA');
    return await response.json();
  },

  remediate: async (reportId) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/rca/reports/${reportId}/remediate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to run remediation');
    return await response.json();
  },
};
